import { useEffect, useRef } from "react";
import { useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet-routing-machine";
import "leaflet-routing-machine/dist/leaflet-routing-machine.css";
import "leaflet-control-geocoder";
import { useTranslation } from 'react-i18next';

// Fix for broken default icons in Leaflet with Webpack/CRA
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

let DefaultIcon = L.icon({
    iconUrl: icon,
    shadowUrl: iconShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41]
});

L.Marker.prototype.options.icon = DefaultIcon;

const RoutingMachine = ({ onRouteFound, waypoints, isDrivingMode }) => {
    const { i18n } = useTranslation();
    const map = useMap();
    const routingControlRef = useRef(null);
    const isCollapsedRef = useRef(false);
    const prevLanguageRef = useRef(i18n.language);

    // Initialize routing control
    useEffect(() => {
        if (!map) return;

        const routingControl = L.Routing.control({
            waypoints: [], // Start empty, update later
            routeWhileDragging: true,
            geocoder: null, // Hide default input boxes (L.Control.Geocoder.nominatim(),)
            router: L.Routing.osrmv1({
                serviceUrl: process.env.REACT_APP_OSRM_SERVICE_URL,
                language: i18n.language === 'fi-tampere' ? 'fi' : i18n.language.split('-')[0]
            }), lineOptions: {
                styles: [{ color: "#6FA1EC", weight: 4 }]
            },
            show: true,
            addWaypoints: false,
            draggableWaypoints: false,
            fitSelectedRoutes: true,
            showAlternatives: false,
            createMarker: function (i, wp, nWps) {
                return L.marker(wp.latLng, {
                    draggable: false,
                    icon: DefaultIcon
                });
            },
            collapsible: true, // Enable collapsible panel
            collapsed: false // Start expanded
        }).addTo(map);

        routingControl.on('routesfound', function (e) {
            const routes = e.routes;
            const route = routes[0];
            if (onRouteFound && route) {
                const plainCoordinates = route.coordinates.map(c => ({ lat: c.lat, lng: c.lng }));
                onRouteFound(plainCoordinates);
            }

            if (route && route.coordinates && route.coordinates.length > 0) {
                const bounds = L.latLngBounds(route.coordinates);
                map.fitBounds(bounds);
            }
        });

        routingControl.on('routeselected', function (e) {
            const route = e.route;
            if (onRouteFound && route) {
                const plainCoordinates = route.coordinates.map(c => ({ lat: c.lat, lng: c.lng }));
                onRouteFound(plainCoordinates);
            }
        });

        routingControlRef.current = routingControl;

        // Hide initially
        const container = routingControl.getContainer();
        if (container) {
            container.style.display = 'none';
        }

        return () => {
            if (map && routingControl) {
                map.removeControl(routingControl);
            }
            routingControlRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [map, onRouteFound]); // Only run on mount/map change or if callback changes

    // Update waypoints when they change
    useEffect(() => {
        if (routingControlRef.current) {
            routingControlRef.current.setWaypoints(waypoints || []);

            // Hide container if not enough waypoints
            const container = routingControlRef.current.getContainer();
            if (container) {
                container.style.display = (waypoints && waypoints.length > 1) ? 'block' : 'none';

                // Reset collapsed state when new route is created
                if (waypoints && waypoints.length > 1 && !isDrivingMode) {
                    isCollapsedRef.current = false;
                }
            }
        }
    }, [waypoints, isDrivingMode]);

    // Update router language when language changes
    useEffect(() => {
        if (routingControlRef.current) {
            const router = routingControlRef.current.getRouter();
            if (router && router.options) {
                const currentLang = i18n.language === 'fi-tampere' ? 'fi' : i18n.language.split('-')[0];
                router.options.language = currentLang;
                // Only force route recalculation if language actually changed
                // (Waypoints update already triggers a route via setWaypoints)
                if (prevLanguageRef.current !== i18n.language && waypoints && waypoints.length > 1) {
                    routingControlRef.current.route();
                }
                prevLanguageRef.current = i18n.language;
            }
        }
    }, [i18n.language, waypoints]);

    // Auto-collapse when entering driving mode
    useEffect(() => {
        if (routingControlRef.current) {
            const container = routingControlRef.current.getContainer();
            if (container && waypoints && waypoints.length > 1) {
                const closeButton = container.querySelector('.leaflet-routing-collapse-btn');

                if (isDrivingMode && !isCollapsedRef.current) {
                    // Collapse when entering driving mode
                    if (closeButton) {
                        closeButton.click();
                        isCollapsedRef.current = true;
                    }
                } else if (!isDrivingMode && isCollapsedRef.current) {
                    // Expand when exiting driving mode
                    if (closeButton) {
                        closeButton.click();
                        isCollapsedRef.current = false;
                    }
                }
            }
        }
    }, [isDrivingMode, waypoints]);

    return null;
};

export default RoutingMachine;
